import { memo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingComponent from "../components/LoadingComponent";
import TableLoader from "../components/TableLoader";
import ExternalIntegrationActions from "../components/ExternalIntegrationActions";
import { useAdminExternalIntegrations } from "../hooks/useAdminExternalIntegrations";
import EditExternalIntegrationModal from "../components/modals/ExternalIntegration/EditExternalIntegrationModal";

/**
 * Main view to manage External Services Integrations Allows viewing
 * the log table, pager, and launching editing or creation manners
 *
 * @returns {JSX.Element} The integrations view component
 */
const AdminExternalIntegration = () => {
  const {
    integrations,
    sellers,
    loading,
    error,
    pagination,
    integrationsCount,
    handleChangePage,
    handleChangeRowsPerPage,
    createIntegration,
    updateIntegration,
    deleteIntegration,
  } = useAdminExternalIntegrations();

  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenEditModal = (integration) => {
    setSelectedIntegration(integration);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIntegration(null);
  };

  const handleTablePageChange = (event, newPage) => {
    handleChangePage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    handleChangeRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleUpdate = async (data) => {
    try {
      await updateIntegration(selectedIntegration.id, data);
      alert("Integración actualizada con éxito");
      handleCloseModal();
    } catch (error) {
      console.error(`Error al actualizar: ${error}`);
    } finally {
      handleCloseModal();
    }
  };

  const handleDelete = async (integrationId) => {
    if (
      window.confirm(
        "¿Estás seguro que quieres eliminar/desactivar esta integración?",
      )
    ) {
      await deleteIntegration(integrationId);
    }
  };

  return (
    <>
      {loading ? <LoadingComponent /> : null}
      {error ? (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      ) : null}

      <Grid item xs={12} className="mg-t-20">
        <h2>Servicios e Integraciones Externas</h2>
      </Grid>

      <ExternalIntegrationActions
        loading={loading}
        error={error}
        createIntegration={createIntegration}
        sellers={sellers}
      />

      <Grid container>
        <Grid container className="custom-table-container">
          <Grid item xs={12} className="flex-center mg-t-0">
            <>
              <TableContainer>
                {loading ? (
                  <TableLoader />
                ) : (
                  <>
                    <Table
                      aria-label="tabla de integraciones"
                      className="fixed-table"
                    >
                      <TableHead sx={{ backgroundColor: "#fff" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Integración
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Tipo
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            URL Destino
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Última Actualización
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }} align="right">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {integrations.map((integration) => (
                          <TableRow
                            key={integration.id}
                            hover
                            sx={{
                              "& > *": { borderBottom: "unset" },
                              backgroundColor: "#fff",
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {integration.integration_name}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Chip
                                size="small"
                                label={integration.integration_type}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row" sx={{ maxWidth: 400, minWidth: 250 }}>
                              <Tooltip title={integration.url} placement="top" arrow>
                                <Typography noWrap variant="body2" sx={{ cursor: "pointer" }}>
                                  {integration.url}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {new Date(
                                integration.updated_at,
                              ).toLocaleDateString()}
                            </TableCell>
                            <TableCell component="th" scope="row" align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(integration)}
                                title="Editar Integración"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(integration.id)}
                                title="Eliminar Integración"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

                {!loading && integrations.length === 0 && (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No se encontraron integraciones configuradas.
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[20, 40, 60, 80, 100]}
                  component="div"
                  count={integrationsCount}
                  rowsPerPage={pagination.rowsPerPage}
                  page={pagination.page - 1}
                  onPageChange={handleTablePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </TableContainer>

              <EditExternalIntegrationModal
                open={isModalOpen}
                sellers={sellers}
                onClose={handleCloseModal}
                onConfirm={handleUpdate}
                initialData={selectedIntegration}
              />
            </>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(AdminExternalIntegration);
